import {
	Box,
	Stack,
	ClickAwayListener,
	Divider,
	FormControlLabel,
	FormGroup,
	InputAdornment,
	List,
	TextField,
	Typography,
	Button,
	Radio,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import { useBoundStore } from 'useStore';
import LightTooltip from 'views/Components/Shared/Components/LightTooltip';
import useCategoryTreeQuery from 'views/Components/Shared/Queries/categoryTreeQuery';

function Options({ closeTooltip, categoryTree }) {
	const selectedCategory = useBoundStore(
		(state) => state.productMasterSelectedCategory
	);
	const setSelectedCategory = useBoundStore(
		(state) => state.setProductMasterSelectedCategory
	);
	const clearProductMasterSelectedCategory = useBoundStore(
		(state) => state.clearProductMasterSelectedCategory
	);

	const [searchField, setSearchField] = useState('');

	//Filtrar categorias segun el campo de busqueda
	const filteredCategories = useMemo(() => {
		if (!searchField) return categoryTree || [];

		return (
			categoryTree?.filter((category) =>
				category.full_path.toLowerCase().includes(searchField.toLowerCase())
			) || []
		);
	}, [categoryTree, searchField]);

	return (
		<List
			sx={{ width: '340px', maxWidth: 340, bgcolor: 'background.paper' }}
			component="nav"
			aria-labelledby="nested-list-subheader"
			subheader={
				<Stack
					direction="column"
					spacing={1}
					alignItems="flex-start"
					justifyContent="space-between"
					marginBottom={2}
				>
					<Typography
						sx={{
							color: 'rgba(0, 0, 0, 0.87)',
							fontFamily: 'Roboto',
							fontStyle: 'normal',
							fontSize: '16px',
							lineHeight: '24px',
							fontWeight: '700',
							letterSpacing: '0.15px',
							padding: '12px',
						}}
					>
						Category Path:
					</Typography>
					<TextField
						value={searchField}
						onChange={(e) => setSearchField(e.target.value)}
						sx={{
							'& .MuiOutlinedInput-notchedOutline': {
								borderRadius: '5px',
								border: '1px solid #0000003B',
							},
							'& .MuiInputBase-input': {
								backgroundColor: '#FFFFFF',
							},
							'& .MuiOutlinedInput-root': {
								backgroundColor: '#FFFFFF',
							},
							width: '100%',
							marginLeft: '8px',
							marginRight: '8px',
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
				</Stack>
			}
		>
			<Divider />

			<List component="div" disablePadding>
				<Box
					sx={{
						maxHeight: '210px',
						overflowY: 'auto',
					}}
				>
					<FormGroup sx={{ pl: 2 }}>
						{filteredCategories?.map((category, index) => (
							<FormControlLabel
								key={category.id}
								sx={{
									borderBottom:
										index === filteredCategories.length - 1
											? 'none'
											: '1px solid rgba(236, 240, 244, 1)',
								}}
								control={
									<Radio
										color="secondary"
										checked={category.full_path === selectedCategory}
										onChange={() => {
											if (selectedCategory === category.full_path) {
												clearProductMasterSelectedCategory();
											} else {
												setSelectedCategory(category.full_path);
											}
										}}
									/>
								}
								label={
									<Typography padding={1} paddingTop={2} paddingBottom={2}>
										{category.full_path}
									</Typography>
								}
							/>
						))}
						{filteredCategories.length === 0 && (
							<Stack
								spacing={0}
								alignItems="center"
								justifyContent="center"
								padding={2}
							>
								<Typography
									sx={{
										color: 'rgba(0, 0, 0, 0.87)',
										fontFamily: 'Roboto',
										fontStyle: 'semi-bold',
										fontSize: '16px',
										lineHeight: '25px',
										fontWeight: '600',
										letterSpacing: '0.20px',
										padding: 1,
										textAlign: 'center',
									}}
								>
									No se encontraron resultados para "{searchField}"
								</Typography>
								<Typography
									sx={{
										color: 'rgba(0, 0, 0, 0.6)',
										fontFamily: 'Roboto',
										fontStyle: 'regular',
										fontSize: '14px',
										lineHeight: '16px',
										fontWeight: '400',
										letterSpacing: '0.17px',
										padding: 1,
										textAlign: 'center',
									}}
								>
									Prueba con otra palabra clave o revisa tu escritura.
								</Typography>
							</Stack>
						)}
					</FormGroup>
				</Box>
			</List>

			<Divider />

			<Button
				color="secondary"
				onClick={closeTooltip}
				sx={{
					mt: 1,
				}}
			>
				CERRAR
			</Button>
		</List>
	);
}

export default function CategoryPathFilter() {
	const [open, setOpen] = useState(false);
	const categoryTreeQuery = useCategoryTreeQuery();

	const handleTooltipClose = () => {
		setOpen(false);
	};

	const handleToggleTooltip = () => {
		setOpen((prev) => !prev);
	};

	return (
		<ClickAwayListener onClickAway={handleTooltipClose}>
			<div>
				<LightTooltip
					PopperProps={{
						disablePortal: true,
					}}
					onClose={handleTooltipClose}
					open={open}
					disableFocusListener
					disableHoverListener
					disableTouchListener
					title={
						<Options
							closeTooltip={handleTooltipClose}
							categoryTree={categoryTreeQuery.data}
						/>
					}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{
							padding: '12px',
							width: '182px',
							borderRadius: '4px',
							border: '1px solid rgba(0, 0, 0, 0.23)',
							cursor: 'pointer',
						}}
						onClick={handleToggleTooltip}
					>
						<Typography
							sx={{
								color: '#00000099',
								fontSize: '16px',
								fontFamily: 'Roboto',
								fontStyle: 'normal',
								fontWeight: '400',
								lineHeight: '24px',
								letterSpacing: '0.15px',
							}}
						>
							Category Path
						</Typography>
						<ArrowDropDownIcon
							sx={{
								fill: '#0000008F',
								width: '24px',
								height: '24px',
							}}
						/>
					</Stack>
				</LightTooltip>
			</div>
		</ClickAwayListener>
	);
}
